import { axiosInstance } from '../utils/axios';

export const getCommentWithUserData = (
  { path: { orgId, streamId, channelId, streamItemId, commentId } },
  extraData,
) =>
  Promise.all([
    // get comment data
    axiosInstance.get(
      `/${orgId}/${streamId}/${channelId}/items/${streamItemId}/comments/${commentId}`,
      {
        ...extraData,
        params: {
          ...extraData?.params,
          refresh: Math.random(),
        },
      },
    ),
    // get comment userData
    axiosInstance.get(
      `/${orgId}/${streamId}/${channelId}/items/${streamItemId}/comments/${commentId}/userdata`,
      {
        ...extraData,
        params: {
          ...extraData?.params,
          refresh: Math.random(),
        },
      },
    ),
  ]);

export const postComment = (
  {
    path: { orgId, streamId, channelId, streamItemId },
    body: { content, inReplyTo, bounty },
    params: { pinnedUntil, isPublic },
  },
  extraData,
) =>
  axiosInstance
    .post(
      `/${orgId}/${streamId}/${channelId}/items/${streamItemId}/comments`,
      {
        content,
        inReplyTo,
        bounty,
      },
      {
        ...extraData,
        params: {
          pinnedUntil,
          private: !isPublic,
        },
      },
    )
    .then(response => {
      // '/{orgId}/{streamId}/{channelId}/items/{streamItemId}/comments/{commentId}'
      const [, , , , , , , commentId] = response.headers.location.split('/');
      return getCommentWithUserData({
        path: { orgId, streamId, channelId, streamItemId, commentId },
      });
    });

export const putComment = (
  {
    path: { orgId, streamId, channelId, streamItemId, inReplyTo },
    body: { content },
    params: { pinnedUntil, isPublic },
  },
  extraData,
) =>
  axiosInstance
    .put(
      `/${orgId}/${streamId}/${channelId}/items/${streamItemId}/comments/${inReplyTo}`,
      {
        // TODO: when the backend will implement the "bounty" field for the comment
        content,
      },
      {
        ...extraData,
        params: {
          pinnedUntil,
          private: !isPublic,
        },
      },
    )
    .then(response => {
      // '/{orgId}/{streamId}/{channelId}/items/{streamItemId}/comments/{commentId}'
      const [, , , , , , , commentId] = response.headers.location.split('/');
      return getCommentWithUserData({
        path: { orgId, streamId, channelId, streamItemId, commentId },
      });
    });

export const getCommentList = (
  {
    path: { orgId, streamId, channelId, streamItemId },
    params: {
      sort = null,
      count = null,
      offset = null,
      pinned = null,
      commentId = null,
      order = null,
      direction = null,
    },
  },
  extraData,
) =>
  Promise.all([
    // get comment list data
    axiosInstance.get(
      `/${orgId}/${streamId}/${channelId}/items/${streamItemId}/comments`,
      {
        ...extraData,
        params: {
          ...extraData?.params,
          sort,
          count,
          offset,
          pinned,
          parent: commentId,
          order,
          direction,
        },
      },
    ),
    // get comment list userData
    axiosInstance.get(
      `/${orgId}/${streamId}/${channelId}/items/${streamItemId}/comments/userdata`,
      {
        ...extraData,
        params: {
          sort,
          count,
          offset,
          pinned,
          parent: commentId,
          order,
          direction,
        },
      },
    ),
  ]);

export const getNestedComments = (
  {
    path: { orgId, streamId, channelId, streamItemId },
    params: { commentId, pinned = null },
  },
  extraData,
) =>
  axiosInstance.get(
    `/${orgId}/${streamId}/${channelId}/items/${streamItemId}/comments`,
    {
      ...extraData,
      params: {
        ...extraData?.params,
        pinned,
        parent: commentId,
      },
    },
  );

export const deleteComment = (
  { path: { orgId, streamId, channelId, streamItemId, commentId } },
  extraData,
) =>
  axiosInstance
    .delete(
      `/${orgId}/${streamId}/${channelId}/items/${streamItemId}/comments/${commentId}`,
      extraData,
    )
    .then(() =>
      getCommentWithUserData({
        path: { orgId, streamId, channelId, streamItemId, commentId },
      }),
    );

export const getCommentLockedContent = (
  { path: { orgId, streamId, channelId, streamItemId, commentId } },
  extraData,
) =>
  axiosInstance.get(
    `/${orgId}/${streamId}/${channelId}/items/${streamItemId}/comments/${commentId}/content`,
    extraData,
  );

export const changeVoteDirection = (
  {
    path: { orgId, streamId, channelId, streamItemId, commentId },
    body: { direction },
  },
  extraData,
) =>
  axiosInstance
    .post(
      `/${orgId}/${streamId}/${channelId}/items/${streamItemId}/comments/${commentId}/votes`,
      { direction },
      extraData,
    )
    .then(() =>
      getCommentWithUserData({
        path: { orgId, streamId, channelId, streamItemId, commentId },
      }),
    );

export const deleteVoteDirection = (
  { path: { orgId, streamId, channelId, streamItemId, commentId } },
  extraData,
) =>
  axiosInstance
    .delete(
      `/${orgId}/${streamId}/${channelId}/items/${streamItemId}/comments/${commentId}/votes`,
      extraData,
    )
    .then(() =>
      getCommentWithUserData({
        path: { orgId, streamId, channelId, streamItemId, commentId },
      }),
    );

export const pinComment = (
  {
    path: { orgId, streamId, channelId, streamItemId, commentId },
    params: { unixTimestamp },
  },
  extraData,
) =>
  axiosInstance
    .post(
      `/${orgId}/${streamId}/${channelId}/items/${streamItemId}/comments/${commentId}/`,
      extraData,
      {
        params: {
          method: 'comment.setPinnedUntil',
          pinnedUntil: unixTimestamp || '',
        },
      },
    )
    .then(() =>
      getCommentWithUserData({
        path: { orgId, streamId, channelId, streamItemId, commentId },
      }),
    );

export const postCommentBookmark = (
  {
    path: { orgId, streamId, channelId, streamItemId, commentId },
    params: { tags },
  },
  extraData,
) =>
  axiosInstance.post(
    `/${orgId}/${streamId}/${channelId}/items/${streamItemId}/comments/${commentId}/bookmark`,
    null,
    {
      ...extraData,
      params: {
        ...extraData?.params,
        tags: tags.join(',') || null,
      },
    },
  );

export const deleteCommentBookmark = (
  { path: { orgId, streamId, channelId, streamItemId, commentId } },
  extraData,
) =>
  axiosInstance
    .delete(
      `/${orgId}/${streamId}/${channelId}/items/${streamItemId}/comments/${commentId}/bookmark`,
      extraData,
    )
    .then(() =>
      getCommentWithUserData({
        path: { orgId, streamId, channelId, streamItemId, commentId },
      }),
    );
